<script>
  /**
   * 🔴 `department` 는 소속·부서·팀을 서버가 공개 규칙대로 합쳐 준 **한 문자열**이다
   * (`User#public_department_label`). 팀을 따로 받지 않는 이유가 이것이다 —
   * 공개 판정이 서버 한 곳에만 있어야 우회가 생기지 않는다.
   *
   * @type {{ displayName: string, username: string, department?: string, bio?: string, theme?: string, isVerified?: boolean, role?: string, badgeColor?: string }}
   */
  let { displayName, username, department = '', bio = '', theme = 'clean', isVerified = false, role = 'user', badgeColor = 'blue' } = $props()

  const isNeubrutalism = $derived(theme === 'neubrutalism')
  const isAdmin = $derived(['super_admin', 'admin', 'moderator'].includes(role))

  const BADGE_COLORS = {
    blue:   '#1d9bf0',
    gold:   '#d4a017',
    mint:   '#10b981',
    purple: '#7c3aed',
    black:  'var(--text-primary)',
    accent: 'var(--accent)',
  }

  const resolvedBadgeColor = $derived(BADGE_COLORS[badgeColor] || BADGE_COLORS.blue)
</script>

<div class="profile-header-info">
  <!-- 이름 -->
  <h1 class="display-name" class:brutal-name={isNeubrutalism}>{displayName}</h1>

  <!-- @username + 뱃지 -->
  <div class="username-row">
    <span class="username">@{username}</span>
    {#if isVerified}
      <span class="badge" style="color: {resolvedBadgeColor}" title="인증된 회원">
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path d="M8.603 3.799A4.49 4.49 0 0112 2.25c1.357 0 2.573.6 3.397 1.549a4.49 4.49 0 013.498 1.307 4.491 4.491 0 011.307 3.497A4.49 4.49 0 0121.75 12a4.49 4.49 0 01-1.549 3.397 4.491 4.491 0 01-1.307 3.497 4.491 4.491 0 01-3.497 1.307A4.49 4.49 0 0112 21.75a4.49 4.49 0 01-3.397-1.549 4.49 4.49 0 01-3.498-1.306 4.491 4.491 0 01-1.307-3.498A4.49 4.49 0 012.25 12c0-1.357.6-2.573 1.549-3.397a4.49 4.49 0 011.307-3.497 4.49 4.49 0 013.497-1.307z" />
          <path fill="white" d="M15.53 9.47a.75.75 0 010 1.06l-4.5 4.5a.75.75 0 01-1.06 0l-2.25-2.25a.75.75 0 111.06-1.06l1.72 1.72 3.97-3.97a.75.75 0 011.06 0z" />
        </svg>
      </span>
    {/if}
    {#if isAdmin}
      <span class="badge" style="color: {BADGE_COLORS.purple}" title="운영진">
        <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
          <path fill-rule="evenodd" d="M12.516 2.17a.75.75 0 00-1.032 0 11.209 11.209 0 01-7.877 3.08.75.75 0 00-.722.515A12.74 12.74 0 002.25 9.75c0 5.942 4.064 10.933 9.563 12.348a.749.749 0 00.374 0c5.499-1.415 9.563-6.406 9.563-12.348 0-1.39-.223-2.73-.635-3.985a.75.75 0 00-.722-.516l-.143.001c-2.996 0-5.717-1.17-7.734-3.08zm3.094 8.016a.75.75 0 10-1.22-.872l-3.236 4.53L9.53 12.22a.75.75 0 00-1.06 1.06l2.25 2.25a.75.75 0 001.14-.094l3.75-5.25z" clip-rule="evenodd" />
        </svg>
      </span>
    {/if}
  </div>

  <!-- 소속 · 부서 · 팀 (서버가 합쳐 준 문자열 그대로) -->
  {#if department}
    <div class="org-row">
      <span class="department-badge">{department}</span>
    </div>
  {/if}

  <!-- 소개글 -->
  {#if bio}
    <p class="bio">{bio}</p>
  {/if}
</div>

<style>
  .profile-header-info {
    text-align: center;
  }

  .display-name {
    font-size: var(--title-size, 22px);
    font-weight: 700;
    color: var(--title-color, var(--text-primary));
    font-family: var(--font-heading, var(--font-body, 'SUIT Variable', sans-serif));
    margin: 0 0 4px;
    letter-spacing: var(--title-letter-spacing, -0.3px);
    line-height: 1.3;
  }

  .brutal-name {
    font-weight: 800;
    font-size: 26px;
  }

  .username-row {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 3px;
    margin-bottom: 8px;
  }

  .username {
    font-size: 14px;
    color: var(--accent);
    font-weight: 500;
  }

  .badge {
    display: inline-flex;
    align-items: center;
    flex-shrink: 0;
  }

  .badge svg {
    width: 16px;
    height: 16px;
  }

  .org-row {
    display: flex;
    justify-content: center;
    gap: 6px;
    margin-bottom: 8px;
    flex-wrap: wrap;
  }

  .department-badge {
    display: inline-block;
    font-size: 12px;
    font-weight: 600;
    color: var(--accent);
    background: color-mix(in srgb, var(--accent) 10%, transparent);
    padding: 3px 10px;
    border-radius: 9999px;
    line-height: 1.4;
    /* 소속/부서/팀 세 항목이 합쳐지면서 문자열이 길어졌다.
       좁은 화면에서 넘칠 때 어절 중간이 잘리지 않게 한다. */
    max-width: 100%;
    word-break: keep-all;
    overflow-wrap: break-word;
  }

  .bio {
    font-size: 14px;
    color: var(--text-secondary);
    margin: 0;
    line-height: 1.6;
    max-width: 320px;
    margin-left: auto;
    margin-right: auto;
  }
</style>
